import React, { useState, useCallback, useEffect } from 'react';
import { GoogleMap, Marker, Polyline } from '@react-google-maps/api';
import { makeStyles } from "@material-ui/core/styles";
import { useAuth } from "../../util/auth";
import PDFTurnByTurnDirections from './PDFTurnByTurnDirections';
import PDFInfo from './PDFInfo';
import { red } from '@material-ui/core/colors';

const useStyles = makeStyles(() => ({
  modalContainer: {
    padding: 10,
    background: "white",
    WebkitFontSmoothing: "antialiased",
    fontSmoothing: "antialiased",
  },

  pdfContainer: {
    backgroundColor: "white",
    color: "black",
    border: "4px solid black",
  },

  // ✅ Export-safe header layout
header: {
  display: "grid",
  gridTemplateColumns: "1fr 520px",
  gap: 16,
  alignItems: "stretch", // ✅ required so headerRightExport gets full height
  width: "100%",
  padding: "5px 15px",
  borderBottom: "2px solid black",
  background: "white",
},

headerRight: {
  // normal (web) behavior
  display: "flex",
  justifyContent: "flex-end",
  alignItems: "center",
  minWidth: 0,
  paddingRight: 10,
},

headerRightExport: {
  // IMPORTANT: must stretch to header row height
  alignSelf: "stretch",
  height: "100%",
  minWidth: 0,

  // table layout is the most stable for dom-to-image vertical centering
  display: "table",
  width: "100%",

  // keep your right padding here (table-cell centering includes it)
  paddingRight: 10,

  // (optional debug)
  // outline: "4px solid lime",
},

productionCellExport: {
  display: "table-cell",
  verticalAlign: "middle",  // ✅ the whole point
  textAlign: "right",
},

productionGridExport: {
  position: "absolute",
  inset: 0,                     // top:0 right:0 bottom:0 left:0
  display: "grid",
  gridTemplateRows: "1fr auto 1fr", // ✅ equal space above & below
  justifyItems: "end",
},

productionName: {
  margin: 0,
  fontWeight: 900,
  lineHeight: 1.05,
  maxWidth: "100%",
  whiteSpace: "normal",
  overflowWrap: "break-word",
  wordBreak: "break-word",
  textAlign: "right",
},

productionNameExport: {
  position: "absolute",
  right: 10,
  top: "50%",
  transform: "translateY(-50%)",
},

headerLeft: {
  minWidth: 0,
  alignSelf: "start",      // ✅ left stays top-aligned
},


  // ✅ matches old: date { fontSize: 1.7em }
  titleLine: {
    margin: 0,
    marginTop: 4,
    fontSize: "1.7em",
    fontWeight: 700,
    lineHeight: 1.15,
  },

  // ✅ matches old: startLocation { fontSize: 1.2em, fontWeight: 400, paddingTop: 10 }
  subLine: {
    margin: 0,
    paddingTop: 8,
    fontSize: "1.2em",
    fontWeight: 400,
    lineHeight: 1.25,

    whiteSpace: "normal",
    overflowWrap: "anywhere",
    wordBreak: "break-word",
  },

  // ✅ matches old: gpsCoords { fontSize: 1.2em, paddingTop: 10 }
  gpsLine: {
    margin: 0,
    paddingTop: 8,
    fontSize: "1.2em",
    fontWeight: 400,
    lineHeight: 1.25,
  },

  mapsRow: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 10,
    width: "100%",
    backgroundColor: "white",
    padding: 8,
  },

  mapBox: {
    height: 450,
    width: "100%",
    backgroundColor: "white",
  },
}));


const PDFMapsDirections = ({ date, set, group, dropdownOrigin, dropdownDestination, allGroupLocations, lostCall, lostCallName, websiteUrl, websiteLinkText, specialInstructions, formatPhoneNumber, setDestinationObject, disableDefaultUI, isExport}) => {
  const auth = useAuth();
  const classes = useStyles();
  const [routePath, setRoutePath] = useState([]);
  const [steps, setSteps] = useState([]);
  const [map, setMap] = useState(null);
  const [satelliteMap, setSatelliteMap] = useState(null);
  const [satTilt, setSatTilt] = useState(0);
  const [gps, setGPS] = useState({});
  const [totalDuration, setTotalDuration] = useState(0)
  const [totalDistance, setTotalDistance] = useState(0)
  const prodName = auth?.user?.productionName || "";
  const len = prodName.length;
  const prodFontSize =
    len <= 18 ? "3em" :
    len <= 30 ? "2.6em" :
    "2.2em";

  // Function to get directions and update the Polyline path
  const fetchDirections = useCallback(() => {
    console.log(auth.user)
    const directionsService = new window.google.maps.DirectionsService();
    directionsService.route(
      {
        origin: dropdownOrigin.mapData.coords,
        destination: dropdownDestination.mapData.coords,
        travelMode: window.google.maps.TravelMode.DRIVING,
      },
      (result, status) => {
        if (status === window.google.maps.DirectionsStatus.OK && result.routes[0]) {
          // Extract the path from the directions result
          // const path = result.routes[0].overview_path.map((point) => ({
          //   lat: point.lat(),
          //   lng: point.lng(),
          // }));
          const path = result.routes[0].legs[0].steps.map((step) => {
            return step.path.map((point) => ({
              lat: point.lat(),
              lng: point.lng(),
            }));
          }).flat(); // Flatten the array of points if needed
          setRoutePath(path); // Update the polyline path state

          // calculate duration
          const totalDuration = result.routes[0].legs.reduce((total, leg) => {
            return total + leg.duration.value; // Add up the duration of each leg
          }, 0);
          setTotalDuration(totalDuration);

          // calculate distance
          const totalDistance = result.routes[0].legs.reduce((total, leg) => {
            return (total + leg.distance.value) / 1609.34;
          }, 0);
          setTotalDistance(totalDistance.toFixed(2));

          // Extract step-by-step directions
          const routeSteps = result.routes[0].legs[0].steps.map((step) => ({
            instructions: step.instructions,
            maneuver: step.maneuver,
            distance: step.distance.text,
            duration: step.duration.text,
          }));
          setSteps(routeSteps);
          // Set bounds based only on the origin and destination locations
          const bounds = new window.google.maps.LatLngBounds();
          bounds.extend(result.routes[0].legs[0].start_location); // Origin
          bounds.extend(result.routes[0].legs[0].end_location);   // Destination
          map?.fitBounds(bounds);
          satelliteMap?.setZoom(18);
        } else {
          setSteps([]);
          console.error('Error fetching directions:', status);
        }
      }
    );
  }, [dropdownDestination.mapData.coords, dropdownOrigin.mapData.coords, map, satelliteMap]);

  const defaultMapLabelOptions = [
    {
      featureType: "all",
      stylers: [{ visibility: "off" }], 
    },
    {
      featureType: "landscape",
      stylers: [{ visibility: "on" }], 
    },
    {
      featureType: "water",
      stylers: [{ visibility: "on" }], 
    },
    {
      featureType: "road",
      stylers: [{ visibility: "on" }], 
    },
    {
      featureType: "transit",
      stylers: [{ visibility: "on" }], 
    },
    {
      featureType: "administrative",
      stylers: [{ visibility: "on" }], 
    },
    {
      featureType: "administrative.land_parcel",
      stylers: [{ visibility: "off" }], 
    },
    {
      featureType: "poi.government",
      stylers: [{ visibility: "on" }], 
    },
    {
      featureType: "poi.park",
      stylers: [{ visibility: "on" }],
    },
    {
      featureType: "poi.school",
      stylers: [{ visibility: "on" }]
    },
    {
      featureType: "poi.attraction",
      stylers: [{ visibility: "on" }]
    }
    ]

  const handleMapLoad = useCallback((mapInstance) => {
    setMap(mapInstance);
  }, []);

  const handleSatelliteMapLoad = useCallback((mapInstance) => {
    setSatelliteMap(mapInstance);
    mapInstance.setMapTypeId('hybrid');
    mapInstance.addListener('tilt_changed', (event) => {
        var tilt = mapInstance.getTilt();
        setSatTilt(tilt);
    });
  }, []);

  const handleOnDragEnd = (event) => {
    const newCoords = {lat: event.latLng.lat(), lng: event.latLng.lng()};
    const jsonCoords = event.latLng.toJSON();
    const geocoder = new window.google.maps.Geocoder();  
    geocoder.geocode({ location: jsonCoords }, (results, status) => {
      if (status === 'OK') {
        const newAddress = results[0].formatted_address;
        // update dest with new coords and addy from onDragEnd
        setDestinationObject(null, {
          ...dropdownDestination,
          mapData: {
            address: newAddress,
            coords: newCoords
          },
        });
      } else {
        console.error('Geocoding failed to get address from drag event:', status);
        // no new addy so just update coords
        setDestinationObject(null, {
          ...dropdownDestination,
          mapData: {
            ...dropdownDestination.mapData, 
              coords: newCoords
          },
        });
      }
    });
  }

  // Fetch directions whenever destination changes
  useEffect(() => {
    fetchDirections();
    // Function to convert latitude and longitude to GPS coordinates in DMS format
    // Helper function to convert decimal to DMS format
    const toDMS = (coordinate) => {
      const absolute = Math.abs(coordinate);
      const degrees = Math.floor(absolute);
      const minutesNotTruncated = (absolute - degrees) * 60;
      const minutes = Math.floor(minutesNotTruncated);
      const seconds = ((minutesNotTruncated - minutes) * 60).toFixed(2);
      return `${degrees}° ${minutes}' ${seconds}"`;
    };
    const latDMS = `${toDMS(dropdownDestination.mapData.coords.lat)} ${dropdownDestination.mapData.coords.lat >= 0 ? 'N' : 'S'}`;
    const lngDMS = `${toDMS(dropdownDestination.mapData.coords.lng)} ${dropdownDestination.mapData.coords.lng >= 0 ? 'E' : 'W'}`;
    setGPS({ lat: latDMS, lng: lngDMS });
  }, [dropdownDestination.mapData.coords.lat, dropdownDestination.mapData.coords.lng, fetchDirections]);

  return (
    <div>
      <div className={classes.modalContainer}>
        <div className={classes.pdfContainer}>

<div className={classes.header}>
  <div className={classes.headerLeft}>
    <p className={classes.titleLine}>
      {new Date(date).toLocaleDateString("en-US", {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
      })}{" "}
      | {group}
    </p>

    <p className={classes.subLine}>
      <span style={{ fontWeight: 900 }}>FROM: </span>
      {dropdownOrigin?.type} - {dropdownOrigin?.name} - {dropdownOrigin?.mapData?.address}
    </p>

    <p className={classes.subLine}>
      <span style={{ fontWeight: 900 }}>TO: </span>
      <span style={{ color: "red" }}>
        {dropdownDestination?.type} - {dropdownDestination?.name} - {dropdownDestination?.mapData?.address}
      </span>
    </p>

    <p className={classes.gpsLine}>
      <span style={{ fontWeight: 900 }}>GPS:</span> {gps?.lat} and {gps?.lng}
    </p>
  </div>

<div className={isExport ? classes.headerRightExport : classes.headerRight}>
  {isExport ? (
    <div className={classes.productionCellExport}>
      <h1 className={classes.productionName} style={{ fontSize: prodFontSize }}>
        {prodName}
      </h1>
    </div>
  ) : (
    <h1 className={classes.productionName} style={{ fontSize: prodFontSize }}>
      {prodName}
    </h1>
  )}
</div>
</div>


        {/* <div className={classes.flexContainerTitles}>
          <div className={classes.toFrom}>
          <h4 className={classes.date}>{new Date(date).toLocaleDateString('en-US', {weekday: 'long',year: 'numeric',month: 'long',day: 'numeric'})} | {group}</h4>
          <div className={classes.startLocation}> <span style={{fontWeight: 900}}>FROM: </span> {dropdownOrigin?.type} - {dropdownOrigin?.name} - {dropdownOrigin?.mapData?.address}</div>
          <div className={classes.startLocation}><span style={{fontWeight: 900}}>TO: </span><span style={{color: 'red'}}>{dropdownDestination?.type} - {dropdownDestination?.name} - {dropdownDestination?.mapData?.address}</span></div>
          <div className={classes.gpsCoords}><span style={{fontWeight: 900}}>GPS:</span> {gps?.lat} and {gps?.lng} </div>

          </div>
            <div style={{
              minWidth: '40%',
              whiteSpace: "nowrap",
              textAlign: 'right',
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center', // ✅ Vertically center within the flex row
            }}>
              <h1 style={{
                marginRight: 20, // ✅ Prevent margin pushing text up
                fontSize: auth?.user?.productionName?.length < 15 ? '3em' : '2.2em',
                lineHeight: 1.1, // ✅ Helps with consistent text height
              }}>
                {auth?.user?.productionName}
              </h1>
            </div>
        </div>  */}

<div className={classes.mapsRow}>
  <div className={classes.mapBox}>
    <GoogleMap
      onLoad={handleMapLoad}
      mapTypeId="hybrid"
      center={dropdownDestination.mapData.coords}
      zoom={12}
      mapContainerStyle={{ height: "100%", width: "100%" }}
      options={{ disableDefaultUI: disableDefaultUI, streetViewControl: false, styles: defaultMapLabelOptions }}
    >

          {/* Custom Polyline for the route */}
          <Polyline path={routePath} options={{ strokeColor: '#007BFF', strokeOpacity: 0.7, strokeWeight: 9, geodesic: true }} />

          {/* Origin Marker */}
          <Marker
            key={'start'}
            position={dropdownOrigin.mapData.coords}
            label={{text:`A`,color:'#fff', fontWeight: 'bold', top: 20}}
          />

          {/* Draggable Destination Marker */}
          <Marker
            key={'finish'}
            position={dropdownDestination.mapData.coords}
            draggable={true}
            label={{text:`B`,color:'#fff', fontWeight: 'bold', top: 20}}
            onDragEnd={(event) => {
              handleOnDragEnd(event)
            }}
          />
        </GoogleMap>
        </div>

  <div className={classes.mapBox}>
    <GoogleMap
      onLoad={handleSatelliteMapLoad}
      center={dropdownDestination.mapData.coords}
      zoom={18}
      mapContainerStyle={{ height: "100%", width: "100%" }}
      options={{ disableDefaultUI: disableDefaultUI, tilt: satTilt, streetViewControl: false, styles: defaultMapLabelOptions }}
    >

          {/* Custom Polyline for the route */}
          <Polyline path={routePath} options={{ strokeColor: '#007BFF', strokeOpacity: 0.7, strokeWeight: 9, geodesic: true }} />

          {/* Origin Marker */}
          <Marker
            key={'start'}
            position={dropdownOrigin.mapData.coords}
            label={{text:`A`,color:'#fff', fontWeight: 'bold', top: 20}}
          />

          {/* Draggable Destination Marker */}
          <Marker
            key={'finish'}
            // zIndex={999}
            position={dropdownDestination.mapData.coords}
            draggable={true}
            label={{text:`B`,color:'#fff', fontWeight: 'bold', top: 20}}
            onDragEnd={(event) => {
              handleOnDragEnd(event)
            }}
          />
        </GoogleMap>
        </div>
        </div>
        <PDFInfo locations={allGroupLocations} isExport={isExport} lostCall={lostCall} lostCallName={lostCallName} specialInstructions={specialInstructions} websiteUrl={websiteUrl} websiteLinkText={websiteLinkText} formatPhoneNumber={formatPhoneNumber} destinationAddress={dropdownDestination?.mapData?.address} destinationName={dropdownDestination?.name} destinationId={dropdownDestination?.id}/>
        <PDFTurnByTurnDirections steps={steps} fromFull={dropdownOrigin} toFull={dropdownDestination} totalDuration={totalDuration} totalDistance={totalDistance} />
        </div>
      </div>
      </div>
  );
};

export default PDFMapsDirections;